import { useCallback, useEffect, useRef, useState } from 'react';

interface Accepted {
  path: string;
}

/**
 * Stabilizes a signed document URL so that a signature-only change (a
 * fresh `Expires`/`Signature`/`Key-Pair-Id` for the exact same file) does
 * not get passed down to `PdfLoader` as a "new" url and trigger a
 * needless re-fetch of the whole PDF.
 *
 * A change to the path (a genuinely different document) is always
 * accepted immediately. A same-path, query-string-only change is held
 * indefinitely — the signed url is comfortably below CloudFront's TTL
 * (typically several minutes+) for the lifetime of a viewing session, so
 * there's no need to time-box the hold. Instead the hold is released only
 * by `reportLoadError`, i.e. when the currently-held url actually fails to
 * load — a real signal that it's gone stale, rather than a fixed timer
 * that has no relation to how long generation actually takes (MED-6986:
 * an earlier 30s timer let one reload slip through whenever the first
 * post-expiry refetch happened to land, which was usually the add-on
 * "ready" burst — timing coincidence, not anything add-on-specific).
 *
 * Returns `[stableUrl, reportLoadError]`. Call `reportLoadError()` when the
 * held url fails to load (e.g. a 403 because the document is still being
 * generated server-side) — it releases the hold so the next re-signed url,
 * which arrives on the normal refetch cadence anyway, is accepted right
 * away instead of leaving the failed load stuck.
 */
export function useStableDocumentUrl(
  url: string | undefined,
): [string | undefined, () => void] {
  const [stableUrl, setStableUrl] = useState(url);
  const lastAccepted = useRef<Accepted | null>(null);
  const holdReleased = useRef(false);

  useEffect(() => {
    // Nothing to hold for an absent url — the render below passes `url`
    // (undefined) straight through rather than routing it through state.
    if (url == null) return;

    const path = url.split('?')[0] ?? '';
    const accepted = lastAccepted.current;
    const isNewDocument = accepted?.path !== path;

    if (isNewDocument || holdReleased.current) {
      lastAccepted.current = { path };
      holdReleased.current = false;
      // Intentional: syncing to an external signal (load success/failure),
      // not deriving from props/state alone — see useLineTyping for the
      // same pattern.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStableUrl(url);
    }
    // Otherwise: same document, signature-only churn — keep the
    // currently-accepted url, suppress the reload.
  }, [url]);

  const reportLoadError = useCallback(() => {
    holdReleased.current = true;
  }, []);

  return [url == null ? url : stableUrl, reportLoadError];
}
